/**
 * Custom mobile navigation — 11th Hour Coffee
 * Manages drawer open/close, L1/L2 level transitions, focus, and keyboard.
 *
 * Keyboard and screen reader access: everything that is not on screen carries the inert attribute
 * (the closed drawer, the hidden levels, the collapsed back bar), so Tab can only reach what is visible.
 * Open: focus moves into the drawer and stays there. Close: focus returns to the menu button.
 */
document.addEventListener('DOMContentLoaded', function () {
  (function () {
    'use strict';

    var drawer = document.getElementById('custom-nav-drawer');
    var overlay = document.getElementById('custom-nav-overlay');
    var closeBtn = document.getElementById('custom-nav-close');
    var backBar = document.getElementById('custom-nav-back');
    var backLabel = document.getElementById('custom-nav-back-label');
    var dots = drawer ? drawer.querySelectorAll('.custom-nav-dot') : [];

    if (!drawer || !overlay || !closeBtn || !backBar || !backLabel) {
      console.warn('custom-mobile-nav: drawer, overlay, close button or back bar is missing from the page; the mobile menu is not set up.');
      return;
    }

    // Mark elements ready so inline <style> allows display
    drawer.classList.add('css-ready');
    overlay.classList.add('css-ready');

    var currentLevel = 1;
    var activeL2 = null;
    var triggerElement = null;
    var sectionHeader = document.querySelector('.section-header');

    // Find Trade's existing hamburger summary button
    var hamburgerSummary = document.querySelector(
      'header-drawer > details > summary.header__icon--menu'
    );

    if (!hamburgerSummary) {
      console.warn('custom-mobile-nav: the header menu button (header-drawer > details > summary.header__icon--menu) was not found; the mobile menu is not set up.');
      return;
    }

    // ── inert helper: inert takes an element out of the tab order and the accessibility tree ──
    function setInert(el, on) {
      if (on) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    }

    // Only the visible level is reachable; the back bar only while a second level is showing.
    function syncInert() {
      drawer.querySelectorAll('.custom-nav-level').forEach(function (level) {
        setInert(level, !level.classList.contains('custom-nav-level--active'));
      });
      setInert(backBar, currentLevel !== 2);
    }

    syncInert();

    // ── Intercept Trade's hamburger ──
    // Capture phase so we fire before Trade's MenuDrawer.onSummaryClick
    hamburgerSummary.addEventListener(
      'click',
      function (e) {
        if (window.innerWidth >= 990) {
          console.warn('custom-mobile-nav: viewport is 990px or wider, so the theme\'s own desktop menu handles this click.');
          return;
        }

        e.preventDefault();
        e.stopImmediatePropagation();

        // Ensure Trade's details doesn't open
        var details = hamburgerSummary.closest('details');
        if (details && details.hasAttribute('open')) {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        }

        openDrawer();
      },
      true
    );

    // ── Open drawer ──
    function openDrawer() {
      triggerElement = hamburgerSummary;
      setInert(drawer, false);
      drawer.classList.add('custom-nav-drawer--open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.setAttribute('aria-modal', 'true');
      overlay.classList.add('custom-nav-overlay--visible');
      overlay.setAttribute('aria-hidden', 'false');
      hamburgerSummary.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Reset to L1
      resetToL1(true);

      // Focus the close button. Focusing right away works because the drawer is no longer inert
      // and the open state makes it visible in the same style recalculation.
      closeBtn.focus({ preventScroll: true });
      if (document.activeElement !== closeBtn) {
        console.warn('custom-mobile-nav: the close button could not take focus when the menu opened.');
      }
    }

    // ── Close drawer ──
    function closeDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'true') {
        console.warn('custom-mobile-nav: close requested but the menu is already closed.');
        return;
      }
      drawer.classList.remove('custom-nav-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.removeAttribute('aria-modal');
      setInert(drawer, true);
      overlay.classList.remove('custom-nav-overlay--visible');
      overlay.setAttribute('aria-hidden', 'true');
      hamburgerSummary.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      // Return focus to the menu button
      if (triggerElement) {
        triggerElement.focus({ preventScroll: true });
        triggerElement = null;
      }
    }

    // ── Close button ──
    closeBtn.addEventListener('click', closeDrawer);

    // ── Overlay click ──
    overlay.addEventListener('click', closeDrawer);

    // ── Escape key ──
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (drawer.getAttribute('aria-hidden') === 'true') return;

      e.preventDefault();
      closeDrawer();
    });

    // ── L1 parent row clicks ──
    var parentRows = drawer.querySelectorAll('.custom-nav-row--parent[data-target]');
    parentRows.forEach(function (row) {
      row.addEventListener('click', function (e) {
        e.preventDefault();
        var target = row.getAttribute('data-target');
        navigateToL2(target);
      });
    });

    // ── Navigate to L2 ──
    function navigateToL2(panelId) {
      var l1 = drawer.querySelector('[data-level="l1"]');
      var l2 = drawer.querySelector('[data-level="' + panelId + '"]');
      if (!l1 || !l2) {
        console.warn('custom-mobile-nav: cannot open panel "' + panelId + '"; the first level or the panel is missing.');
        return;
      }

      currentLevel = 2;
      activeL2 = panelId;

      // Animate L1 out left
      l1.classList.remove('custom-nav-level--active');
      l1.classList.add('custom-nav-level--exit');

      // Animate L2 in from right
      l2.classList.remove('custom-nav-level--right', 'custom-nav-level--exit');
      l2.classList.add('custom-nav-level--active');

      // Update dots
      updateDots(2);
      syncInert();

      // Show back bar
      var parentLabel = l2.getAttribute('data-parent-label') || '';
      backLabel.textContent = parentLabel;
      backBar.classList.add('custom-nav-back--visible');

      // Focus first nav row in L2 (the panel turns visible immediately, so no delay is needed)
      var firstRow = l2.querySelector('.custom-nav-row');
      if (firstRow) {
        firstRow.focus({ preventScroll: true });
      } else {
        console.warn('custom-mobile-nav: panel "' + panelId + '" has no rows to focus.');
      }
    }

    // ── Back bar ──
    backBar.addEventListener('click', goBack);
    backBar.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        goBack();
      }
    });

    function goBack() {
      if (currentLevel !== 2 || !activeL2) {
        console.warn('custom-mobile-nav: back requested but no second level is open.');
        return;
      }

      var l1 = drawer.querySelector('[data-level="l1"]');
      var l2 = drawer.querySelector('[data-level="' + activeL2 + '"]');
      if (!l1 || !l2) {
        console.warn('custom-mobile-nav: cannot go back; the first level or the open panel is missing.');
        return;
      }

      currentLevel = 1;

      // Animate L2 out right
      l2.classList.remove('custom-nav-level--active', 'custom-nav-level--exit');
      l2.classList.add('custom-nav-level--right');

      // Animate L1 back in
      l1.classList.remove('custom-nav-level--exit', 'custom-nav-level--right');
      l1.classList.add('custom-nav-level--active');

      // Update dots
      updateDots(1);
      syncInert();

      // Hide back bar
      backBar.classList.remove('custom-nav-back--visible');

      // Focus the parent row that opened this L2
      var parentRow = drawer.querySelector('[data-target="' + activeL2 + '"]');
      if (parentRow) {
        parentRow.focus({ preventScroll: true });
      } else {
        console.warn('custom-mobile-nav: no row found for panel "' + activeL2 + '" to return focus to.');
      }
      activeL2 = null;
    }

    // ── Reset to L1 (instant, no animation) ──
    function resetToL1(instant) {
      currentLevel = 1;
      activeL2 = null;

      var allLevels = drawer.querySelectorAll('.custom-nav-level');
      allLevels.forEach(function (level) {
        level.classList.remove(
          'custom-nav-level--active',
          'custom-nav-level--exit',
          'custom-nav-level--right'
        );
        if (level.getAttribute('data-level') === 'l1') {
          level.classList.add('custom-nav-level--active');
        }
      });

      updateDots(1);
      syncInert();
      backBar.classList.remove('custom-nav-back--visible');
    }

    // ── Update level dots ──
    function updateDots(level) {
      dots.forEach(function (dot) {
        var dotLevel = parseInt(dot.getAttribute('data-dot'), 10);
        if (dotLevel <= level) {
          dot.classList.add('custom-nav-dot--active');
        } else {
          dot.classList.remove('custom-nav-dot--active');
        }
      });
    }

    // ── Focus trap ──
    // Listens on the document so it also catches Tab when focus is outside the drawer.
    // Inert (hidden) levels are skipped, so the ends are always the visible first and last controls.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (drawer.getAttribute('aria-hidden') === 'true') return;

      var focusable = Array.prototype.filter.call(
        drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
        function (el) {
          return !el.closest('[inert]');
        }
      );
      if (focusable.length === 0) {
        console.warn('custom-mobile-nav: no focusable controls inside the open menu; keeping focus on the menu.');
        e.preventDefault();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (!drawer.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    });
  })();
});
